import { msalInstance } from './msalInit';

/**
 * Check if the current user is a member of a specific group
 * 
 * This function relies on the 'groups' claim being included in the ID token.
 * You must configure this in Azure Portal:
 * - App registrations > Your app > Token configuration
 * - Add groups as an optional claim to ID tokens
 * - Set additionalProperties to include group display names (cloud_displayname)
 *
 * @param groupName The name of the group to check membership for
 * @returns boolean indicating if the user is a member of the specified group
 */
export const isUserInGroup = (groupName: string): boolean => {
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      console.warn('No active account found when checking group membership');
      return false;
    }

    // Get the current account
    const currentAccount = accounts[0];
    
    // Check if the ID token claims include groups
    const groups = currentAccount.idTokenClaims?.groups;
    
    if (!groups) {
      console.warn('No groups claim found in ID token. Make sure groups are configured as a claim in Azure portal.');
      return false;
    }
    
    // Check if the user belongs to the specified group
    if (Array.isArray(groups)) {
      return groups.includes(groupName);
    } else if (typeof groups === 'string') {
      // In some cases, groups might be a single string
      return groups === groupName;
    }
    
    return false;
  } catch (error) {
    console.error('Error checking group membership:', error);
    return false;
  }
};

/**
 * Get all groups the user belongs to from their ID token
 * 
 * @returns An array of group names or undefined if no groups found
 */
export const getUserGroups = (): string[] | undefined => {
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      return undefined;
    }

    // Get the groups claim from the ID token
    const groups = accounts[0].idTokenClaims?.groups;
    
    if (Array.isArray(groups)) {
      return groups;
    } else if (typeof groups === 'string') {
      return [groups];
    }
    
    return undefined;
  } catch (error) {
    console.error('Error getting user groups:', error);
    return undefined;
  }
};

/**
 * Check if Azure AD returned an "overage" indicator for groups
 * 
 * If a user belongs to too many groups, Azure AD may not include all groups in the token
 * and instead include an "overage" indicator. In this case, you should use Microsoft Graph
 * to get the complete list of groups.
 * 
 * @returns boolean indicating if groups were too many to include in token
 */
export const hasGroupOverage = (): boolean => {
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      return false;
    }
    
    // Check for _claim_names and _claim_sources in the token
    // These properties indicate claims were too large and should be retrieved separately
    const claimNames = accounts[0].idTokenClaims?._claim_names as { groups?: string } | undefined;
    
    return !!(claimNames && claimNames.groups);
  } catch (error) {
    console.error('Error checking for group overage:', error);
    return false;
  }
};

/**
 * Clear all authentication state and storage
 * 
 * This function is useful when there are auth problems or when you want to reset the auth state.
 * It clears both sessionStorage and localStorage items related to MSAL, as well as auth cookies.
 * 
 * @returns Promise that resolves when cleanup is complete
 */
export const clearAuthState = async (): Promise<void> => {
  try {

    
    // First try to logout from MSAL (this won't redirect)
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      try {
        // Try to logout locally without redirect
        await msalInstance.logoutPopup({
          mainWindowRedirectUri: window.location.origin,
          postLogoutRedirectUri: window.location.origin,
          account: accounts[0]
        });
      } catch (e) {
        // Ignore errors from popup logout
        console.warn('Popup logout failed, continuing with cleanup', e);
      }
    }

    // Clear MSAL cache
    msalInstance.clearCache();
    
    // Clear interaction in progress flags
    const msalStorageKeys = Object.keys(sessionStorage)
      .filter(k => k.includes('msal') || k.includes('login') || k.includes('token') || k.includes('auth'));
    
    msalStorageKeys.forEach(key => {
      try {
        sessionStorage.removeItem(key);
      } catch (e) {
        console.warn(`Failed to remove session storage key: ${key}`, e);
      }
    });
    
    // Also clear from localStorage (in case cacheLocation was previously localStorage)
    const msalLocalStorageKeys = Object.keys(localStorage)
      .filter(k => k.includes('msal') || k.includes('login') || k.includes('token') || k.includes('auth'));
    
    msalLocalStorageKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.warn(`Failed to remove local storage key: ${key}`, e);
      }
    });
    
    // Clear all cookies related to auth
    document.cookie.split(';').forEach(cookie => {
      const [name] = cookie.trim().split('=');
      if (name.includes('msal') || name.includes('auth') || name.includes('login')) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      }
    });
    

  } catch (error) {
    console.error('Error clearing authentication state:', error);
    throw error;
  }
}; 